import React, { useEffect, useState } from "react";
import { FileUploader } from "react-drag-drop-files";
import { AxiosError, AxiosResponse } from "axios";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { uploadFieldServiceSoftware } from "@/api/uploadFieldServiceSoftware/uploadFieldServiceSoftwareApi";
import Gleap from "gleap";
import {
  HelpCircle,
  Cloud,
  Settings,
  Upload,
  AlertTriangle,
} from "lucide-react";
import { useNotification } from "@/context/NotificationContext";
import { useSubscription } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/app/store";
import { fetchIntegrationsStatusAction } from "@/modules/hub/features/UserAppSettings/slices/integrationsStatusSlice";
import { RootState } from "@/app/rootReducer";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { ON_MEMBERS_STREAM_COUNT_SUBSCRIPTION } from "@/modules/stochastic/features/FieldServiceImport/graphql/subscriptions/onMembersStreamCount";
import { MembersStreamCountSubscriptionData } from "@/modules/stochastic/features/FieldServiceImport/graphql/subscriptions/onMembersStreamCount/types";
import { ON_COMPANY_DATA_IMPORT_JOB_IN_PROGRESS_COUNT_SUBSCRIPTION } from "@/modules/stochastic/features/CampaignManagement/graphql/subscriptions/onCompanyDataImportJobInProgressCount";
import { CompanyDataImportJobInProgressCountSubscriptionData } from "@/modules/stochastic/features/CampaignManagement/graphql/subscriptions/types";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { validateServiceTitanBusinessUnitMappings } from "@/api/validateServiceTitanBusinessUnitMappings/validateServiceTitanBusinessUnitMappingsApi";
import { Link } from "react-router-dom";
import { ValidateServiceTitanBusinessUnitMappingsResponse } from "@/api/validateServiceTitanBusinessUnitMappings/types";
import { fetchServiceTitanCredential } from "@/api/fetchServiceTitanCredential/fetchServiceTitanCredentialApi";
import { ServiceTitanCredential } from "@/api/fetchServiceTitanCredential/types";
import { triggerServiceTitanImport } from "@/api/triggerServiceTitanImport/triggerServiceTitanImportApi";
import { triggerQBOImport } from "@/api/triggerQBOImport/triggerQBOImportApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePurgeInvoices } from "@/modules/stochastic/features/PurgeInvoices/hooks/usePurgeInvoices";
import PurgeInvoicesModal from "../PurgeInvoicesModal/PurgeInvoicesModal";
import permissionsService from "@/services/permissionsService";

const fileTypes = ["CSV", "XLSX", "XLS"];
const softwarePackages = [
  { value: "ServiceTitan", label: "ServiceTitan" },
  { value: "Other", label: "Other" },
];
type ActiveIntegration = "servicetitan" | "quickbooks_online" | "file_import";
const importTypes = [
  {
    value: "activeCustomers",
    label: "Active Customers (Include ALL customers)",
    description: null,
  },
  {
    value: "invoices",
    label: "Invoices (Include invoices since last upload)",
    description: null,
  },
];

interface TradeOption {
  label: string;
  value: string;
}

interface FieldServiceImportProps {
  supportedTrades?: TradeOption[];
  onUploadComplete?: (jobId: string) => void;
}

export const FieldServiceImport: React.FC<FieldServiceImportProps> = ({
  supportedTrades = [
    { label: "HVAC", value: "hvac" },
    { label: "Electrical", value: "electrical" },
    { label: "Plumbing", value: "plumbing" },
    { label: "Roofing", value: "roofing" },
  ],
}) => {
  const { showNotification } = useNotification();
  const { hasPermission } = permissionsService();
  const dispatch = useDispatch<AppDispatch>();
  const userAppSettings = useSelector(
    (state: RootState) => state.userAppSettings.userAppSettings,
  );
  const { integrationsStatus } = useSelector(
    (state: RootState) => state.integrationsStatus,
  );
  const tenantId = userAppSettings?.intacctId;
  const companyId = userAppSettings?.companyId ?? 0;
  const { handlePurgeInvoices, purgeLoading } = usePurgeInvoices();

  const activeIntegration = integrationsStatus?.activeIntegration as
    | ActiveIntegration
    | undefined;

  // Check permissions
  const canManageIntegrations = hasPermission("integrations");
  const canPurgeInvoices = hasPermission("stochastic-purge-invoices");

  const { data: memberStreamData, error: memberStreamError } =
    useSubscription<MembersStreamCountSubscriptionData>(
      ON_MEMBERS_STREAM_COUNT_SUBSCRIPTION,
      {
        variables: { tenantId: tenantId ?? "" },
        skip: !tenantId,
      },
    );

  const { data: importData } =
    useSubscription<CompanyDataImportJobInProgressCountSubscriptionData>(
      ON_COMPANY_DATA_IMPORT_JOB_IN_PROGRESS_COUNT_SUBSCRIPTION,
      {
        variables: { companyId },
        skip: !companyId,
      },
    );

  const inProgressCount =
    importData?.company_data_import_job_aggregate?.aggregate?.count ?? 0;
  const isImportInProgress = inProgressCount > 0;

  useEffect(() => {
    if (memberStreamData) {
      console.log(
        "Subscription data for members_stream count:",
        memberStreamData,
      );
    }
  }, [memberStreamData]);

  useEffect(() => {
    if (memberStreamError) {
      console.error(
        "Members stream count subscription error:",
        memberStreamError.message,
      );
    }
  }, [memberStreamError]);

  useEffect(() => {
    dispatch(fetchIntegrationsStatusAction());
  }, [dispatch]);

  const [selectedSoftware, setSelectedSoftware] = useState("ServiceTitan");
  const [selectedTrade, setSelectedTrade] = useState(
    supportedTrades.length === 1 ? supportedTrades[0].value : "",
  );
  const [selectedImportType, setSelectedImportType] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [, setSelectedFile] = useState<File | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isApiImporting, setIsApiImporting] = useState(false);
  const [isQboImporting, setIsQboImporting] = useState(false);
  const [serviceTitanCredential, setServiceTitanCredential] =
    useState<ServiceTitanCredential | null>(null);
  const [checkingCredentials, setCheckingCredentials] = useState(false);
  const [showPurgeConfirmationModal, setShowPurgeConfirmationModal] =
    useState(false);

  const [dialogTitleContent, setDialogTitleContent] =
    useState<React.ReactNode>("");
  const [dialogInstructionTitle, setDialogInstructionTitle] =
    useState<React.ReactNode>("");
  const [dialogInstructionItems, setDialogInstructionItems] = useState<
    string[]
  >([]);
  const [dialogInstructionFinalQuestion, setDialogInstructionFinalQuestion] =
    useState<string>("");
  const [cancelMessage, setCancelMessage] = useState<string>("");
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [cancelHandleFunction, setCancelHandleFunction] = useState<() => void>(
    () => () => {},
  );
  const [confirmHandleFunction, setConfirmHandleFunction] = useState<
    () => void
  >(() => () => {});

  // Check for ServiceTitan credentials when activeIntegration is servicetitan
  useEffect(() => {
    const checkServiceTitanCredentials = async () => {
      if (activeIntegration === "servicetitan") {
        // Skip API call if user doesn't have permission
        if (!canManageIntegrations) {
          setServiceTitanCredential(null);
          return;
        }

        const companyUuid = localStorage.getItem("selectedCompanyUuid");
        if (!companyUuid) {
          // Set to null if no company UUID to show the configuration UI
          setServiceTitanCredential(null);
          return;
        }

        setCheckingCredentials(true);
        try {
          const response = await fetchServiceTitanCredential(companyUuid);

          if (response.data && response.data.credential) {
            setServiceTitanCredential(response.data.credential);
          } else {
            setServiceTitanCredential(null);
          }
        } catch {
          setServiceTitanCredential(null);
        } finally {
          setCheckingCredentials(false);
        }
      } else {
        // Reset state when not servicetitan
        setServiceTitanCredential(null);
      }
    };

    checkServiceTitanCredentials();
  }, [activeIntegration, canManageIntegrations]);

  const handleUploadButton = async () => {
    const isTradeRequired = supportedTrades.length > 1;

    if (
      !file ||
      !selectedSoftware ||
      !selectedImportType ||
      (isTradeRequired && !selectedTrade)
    ) {
      const message = "Please fill in all required fields";
      alert(message);
      showNotification("Missing Fields", message, "error");
      return;
    }

    // Check if ServiceTitan business units are mapped to trades
    if (selectedSoftware === "ServiceTitan") {
      try {
        const companyUuid = localStorage.getItem("selectedCompanyUuid");
        if (!companyUuid) {
          showNotification("Error", "Company information not found", "error");
          return;
        }

        const validationResult =
          (await validateServiceTitanBusinessUnitMappings(
            companyUuid,
          )) as ValidateServiceTitanBusinessUnitMappingsResponse;
        console.log("validationResult full response:", validationResult);

        // Handle the response structure
        const isValid = validationResult?.isValid ?? true;
        const unmappedCount = validationResult?.unmappedCount ?? 0;

        if (!isValid) {
          const editCompanyPath = `/admin/companies/${companyUuid}/edit`;

          const errorMessage = (
            <div>
              {unmappedCount > 0 ? (
                <>
                  You have {unmappedCount} unmapped ServiceTitan Business Unit
                  {unmappedCount !== 1 ? "s" : ""}. You must map each
                  ServiceTitan Business Unit to a Trade before uploading.{" "}
                  <Link
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setShowConfirmationModal(false)}
                    state={{ defaultTab: "servicetitan" }}
                    to={editCompanyPath}
                  >
                    Click here to go to Company Management
                  </Link>{" "}
                  and click on the ServiceTitan tab to complete the mapping.
                </>
              ) : (
                <>
                  All ServiceTitan Business Units must be mapped to a Trade
                  uploading.{" "}
                  <Link
                    className="text-blue-600 hover:text-blue-800 underline"
                    onClick={() => setShowConfirmationModal(false)}
                    state={{ defaultTab: "servicetitan" }}
                    to={editCompanyPath}
                  >
                    Click here to go to Company Management
                  </Link>{" "}
                  and click on the ServiceTitan tab to complete the mapping.
                </>
              )}
            </div>
          );

          setDialogTitleContent("Business Unit Mapping Required");
          setDialogInstructionTitle(errorMessage);
          setDialogInstructionItems([]);
          setDialogInstructionFinalQuestion("");
          setCancelMessage("Close");
          setConfirmMessage("");
          setCancelHandleFunction(() => handleUploadCancelModal);
          setConfirmHandleFunction(() => () => {});
          setShowConfirmationModal(true);
          return;
        }
      } catch (error) {
        console.error("Error validating business unit mappings:", error);
        showNotification(
          "Error",
          "Failed to validate business unit mappings",
          "error",
        );
        return;
      }
    }

    if (selectedImportType === "activeCustomers") {
      setDialogTitleContent("Upload Active Customers");
      setDialogInstructionTitle(
        "You are about to upload a new customer list. This will:",
      );
      setDialogInstructionItems([
        "Mark all existing customers as inactive",
        "Set only the customers in your new list as active",
        "Preserve all historical invoice data",
      ]);
      setDialogInstructionFinalQuestion("Are you sure you want to proceed?");
      setConfirmMessage("Confirm Upload");
      setCancelMessage("Cancel");
      setCancelHandleFunction(() => handleUploadCancelModal);
      setConfirmHandleFunction(() => handleUploadConfirmModal);
      setShowConfirmationModal(true);
    } else {
      doActualUpload();
    }
  };

  const doActualUpload = async () => {
    if (!file) return;
    setUploadError(null);
    setUploadSuccess(false);
    setSelectedFile(file);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      await uploadFieldServiceSoftware(
        {
          file,
          software: selectedSoftware,
          trade: selectedTrade,
          importType: selectedImportType,
        },
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1),
          );
          setUploadProgress(percentCompleted);
        },
      );

      setUploadSuccess(true);
      setFile(null);
      setSelectedSoftware("");
      setSelectedTrade("");
      setSelectedImportType("");
      setSelectedFile(null);
      showNotification("Success", `File successfully uploaded!`, "success");
    } catch (err) {
      let errorMessage = "An error occurred.";
      try {
        const typedError = err as AxiosError;
        if (typedError.response) {
          errorMessage = await extractErrorMessage(
            typedError.response as AxiosResponse,
          );
        }
      } catch (parseErr) {
        console.error("Error parsing upload error:", parseErr);
      }
      setUploadError(errorMessage);
      setDialogTitleContent("Upload Error");
      setDialogInstructionTitle(errorMessage);
      setDialogInstructionItems([]);
      setDialogInstructionFinalQuestion("");
      setCancelMessage("Close");
      setConfirmMessage("");
      setCancelHandleFunction(() => handleUploadCancelModal);
      setConfirmHandleFunction(() => () => {});
      setShowConfirmationModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (isUploading || uploadSuccess || uploadError) {
      document
        .getElementById("upload-status")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [isUploading, uploadSuccess, uploadError]);

  const handleUploadConfirmModal = async () => {
    setShowConfirmationModal(false);
    await doActualUpload();
  };

  const handleUploadCancelModal = () => {
    setShowConfirmationModal(false);
  };

  const handleApiImport = async () => {
    // Check if ServiceTitan business units are mapped to trades
    try {
      const companyUuid = localStorage.getItem("selectedCompanyUuid");
      if (!companyUuid) {
        showNotification("Error", "Company information not found", "error");
        return;
      }

      const validationResult = (await validateServiceTitanBusinessUnitMappings(
        companyUuid,
      )) as ValidateServiceTitanBusinessUnitMappingsResponse;
      console.log("validationResult full response:", validationResult);

      // Handle the response structure
      const isValid = validationResult?.isValid ?? true;
      const unmappedCount = validationResult?.unmappedCount ?? 0;

      if (!isValid) {
        const editCompanyPath = `/admin/companies/${companyUuid}/edit`;

        const errorMessage = (
          <div>
            {unmappedCount > 0 ? (
              <>
                You have {unmappedCount} unmapped ServiceTitan Business Unit
                {unmappedCount !== 1 ? "s" : ""}. You must map each ServiceTitan
                Business Unit to a Trade before importing.{" "}
                <Link
                  className="text-blue-600 hover:text-blue-800 underline"
                  onClick={() => setShowConfirmationModal(false)}
                  state={{ defaultTab: "servicetitan" }}
                  to={editCompanyPath}
                >
                  Click here to go to Company Management
                </Link>{" "}
                and click on the ServiceTitan tab to complete the mapping.
              </>
            ) : (
              <>
                All ServiceTitan Business Units must be mapped to a Trade before
                importing.{" "}
                <Link
                  className="text-blue-600 hover:text-blue-800 underline"
                  onClick={() => setShowConfirmationModal(false)}
                  state={{ defaultTab: "servicetitan" }}
                  to={editCompanyPath}
                >
                  Click here to go to Company Management
                </Link>{" "}
                and click on the ServiceTitan tab to complete the mapping.
              </>
            )}
          </div>
        );

        setDialogTitleContent("Business Unit Mapping Required");
        setDialogInstructionTitle(errorMessage);
        setDialogInstructionItems([]);
        setDialogInstructionFinalQuestion("");
        setCancelMessage("Close");
        setConfirmMessage("");
        setCancelHandleFunction(() => handleUploadCancelModal);
        setConfirmHandleFunction(() => () => {});
        setShowConfirmationModal(true);
        return;
      }
    } catch (error) {
      console.error("Error validating business unit mappings:", error);
      showNotification(
        "Error",
        "Failed to validate business unit mappings",
        "error",
      );
      return;
    }

    // Proceed with API import
    setIsApiImporting(true);
    try {
      const companyUuid = localStorage.getItem("selectedCompanyUuid");
      if (!companyUuid) {
        showNotification("Error", "Company information not found", "error");
        return;
      }

      // Check if we have a credential
      if (!serviceTitanCredential) {
        showNotification("Error", "ServiceTitan credential not found", "error");
        return;
      }

      showNotification(
        "Info",
        "Starting ServiceTitan data import...",
        "success",
      );

      const syncResponse = await triggerServiceTitanImport(companyUuid, {
        credentialId: serviceTitanCredential.id,
        syncType: "manual",
        syncDataType: "both", // Import both customers and invoices
      });

      if (syncResponse?.success) {
        showNotification(
          "Success",
          syncResponse.message ||
            "ServiceTitan data import initiated successfully!",
          "success",
        );
        setSelectedImportType("");
      } else {
        showNotification(
          "Error",
          syncResponse?.message ||
            "Failed to initiate ServiceTitan data import",
          "error",
        );
      }
    } catch (error) {
      console.error("API import error:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to initiate ServiceTitan sync";
      showNotification("Error", errorMessage, "error");
    } finally {
      setIsApiImporting(false);
    }
  };

  const handleQboImport = async () => {
    setIsQboImporting(true);
    try {
      const companyUuid = localStorage.getItem("selectedCompanyUuid");
      if (!companyUuid) {
        showNotification("Error", "Company information not found", "error");
        return;
      }

      showNotification(
        "Info",
        "Starting QuickBooks Online data import...",
        "success",
      );

      const syncResponse = await triggerQBOImport(companyUuid, {
        syncType: "manual",
      });

      if (syncResponse?.success) {
        showNotification(
          "Success",
          syncResponse.message ||
            "QuickBooks Online data import initiated successfully!",
          "success",
        );
      } else {
        showNotification(
          "Error",
          syncResponse?.message ||
            "Failed to initiate QuickBooks Online data import",
          "error",
        );
      }
    } catch (error) {
      console.error("QBO import error:", error);
      let errorMessage = "Failed to initiate QuickBooks Online sync";
      const typedError = error as AxiosError<{
        errors?: { detail?: string };
      }>;
      if (typedError.response?.data?.errors?.detail) {
        errorMessage = typedError.response.data.errors.detail;
      }
      showNotification("Error", errorMessage, "error");
    } finally {
      setIsQboImporting(false);
    }
  };

  const handlePurgeConfirmModal = async () => {
    setShowPurgeConfirmationModal(false);
    if (tenantId) {
      await handlePurgeInvoices(tenantId);
    }
  };

  const handlePurgeCancelModal = () => {
    setShowPurgeConfirmationModal(false);
  };

  return (
    <MainPageWrapper
      actions={
        canPurgeInvoices ? (
          <Button
            disabled={!tenantId || purgeLoading || isImportInProgress}
            onClick={() => {
              setShowPurgeConfirmationModal(true);
            }}
          >
            {purgeLoading ? "Purging..." : "Purge Imported Invoices"}
          </Button>
        ) : null
      }
      title="Field Service Import"
      titleHelpIcon={
        <HelpCircle
          className="w-5 h-5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
          onClick={() => Gleap.openHelpCenterCollection("3", false)}
        />
      }
    >
      <div>
        {activeIntegration === "file_import" && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Card className="max-w-md w-full">
              <CardContent className="pt-6 space-y-5">
                <div>
                  <label className="block mb-1.5 text-sm text-gray-500 dark:text-gray-400">
                    Software Package
                  </label>
                  <select
                    className="w-full h-9 px-3 text-sm border border-gray-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-ring"
                    onChange={(e) => setSelectedSoftware(e.target.value)}
                    value={selectedSoftware}
                  >
                    <option value="">Select software package</option>
                    {softwarePackages.map((software) => (
                      <option key={software.value} value={software.value}>
                        {software.label}
                      </option>
                    ))}
                  </select>
                </div>

                {supportedTrades.length > 1 && (
                  <div>
                    <label className="block mb-1.5 text-sm text-gray-500 dark:text-gray-400">
                      Trade
                    </label>
                    <select
                      className="w-full h-9 px-3 text-sm border border-gray-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-ring"
                      onChange={(e) => setSelectedTrade(e.target.value)}
                      value={selectedTrade}
                    >
                      <option value="">Select trade</option>
                      {supportedTrades.map((trade) => (
                        <option key={trade.value} value={trade.value}>
                          {trade.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block mb-2 text-sm text-gray-500 dark:text-gray-400">
                    Import Type
                  </label>
                  <div className="space-y-1.5">
                    {importTypes.map((type) => (
                      <label
                        className="flex items-center gap-2.5 p-2.5 rounded-md border border-transparent cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-slate-700/50 has-[:checked]:border-gray-200 has-[:checked]:bg-gray-50 dark:has-[:checked]:border-slate-700 dark:has-[:checked]:bg-slate-700/50"
                        key={type.value}
                      >
                        <input
                          checked={selectedImportType === type.value}
                          className="accent-primary"
                          name="importType"
                          onChange={(e) =>
                            setSelectedImportType(e.target.value)
                          }
                          type="radio"
                          value={type.value}
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-200">
                          {type.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block mb-1.5 text-sm text-gray-500 dark:text-gray-400">
                    File
                  </label>
                  <FileUploader
                    handleChange={(f: File) => setFile(f)}
                    maxSize={100}
                    name="file"
                    types={fileTypes}
                    uploadedLabel="Uploaded Successfully!"
                  />
                  {file && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      Selected: {file.name}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    disabled={!file || isUploading || !selectedImportType}
                    onClick={handleUploadButton}
                  >
                    <Upload className="w-4 h-4" />
                    {isUploading ? "Uploading..." : "Upload File"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeIntegration === "servicetitan" && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Card className="max-w-md w-full">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                {canManageIntegrations ? (
                  <>
                    {checkingCredentials ? (
                      <>
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 dark:border-slate-700 border-t-blue-500 dark:border-t-blue-400" />
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
                          Checking ServiceTitan credentials...
                        </p>
                      </>
                    ) : serviceTitanCredential === null ? (
                      <>
                        <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-5">
                          <Cloud className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                        </div>
                        <h3 className="text-base font-normal text-gray-900 dark:text-white mb-2">
                          ServiceTitan integration not configured
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
                          Connect your ServiceTitan account to enable automatic
                          data synchronization.
                        </p>
                        <Button
                          onClick={() => {
                            try {
                              const companyUuid = localStorage.getItem(
                                "selectedCompanyUuid",
                              );
                              const companyName = localStorage.getItem(
                                "selectedCompanyName",
                              );
                              const userEmail =
                                localStorage.getItem("userEmail");

                              if (
                                typeof Gleap !== "undefined" &&
                                Gleap.preFillForm
                              ) {
                                Gleap.preFillForm({
                                  companyId: companyUuid || "",
                                  companyName: companyName || "",
                                  userEmail: userEmail || "",
                                  integrationType: "ServiceTitan",
                                  requestType: "configure_integration",
                                  tenantId: tenantId || "",
                                  subject:
                                    "ServiceTitan Integration Configuration Request",
                                });
                              }

                              if (
                                typeof Gleap !== "undefined" &&
                                Gleap.attachCustomData
                              ) {
                                Gleap.attachCustomData({
                                  formType: "serviceTitanIntegration",
                                  companyUuid: companyUuid || "",
                                  tenantId: tenantId || "",
                                  timestamp: new Date().toISOString(),
                                  source: "field_service_import_page",
                                });
                              }

                              if (
                                typeof Gleap !== "undefined" &&
                                Gleap.startClassicForm
                              ) {
                                Gleap.startClassicForm(
                                  "d13a831fd9bc4715b310",
                                  false,
                                );
                              } else {
                                Gleap.open();
                              }
                            } catch {
                              showNotification(
                                "Configuration Form Error",
                                "Unable to open the configuration form. Please try again or contact support.",
                                "error",
                              );
                            }
                          }}
                          variant="outline"
                        >
                          <Settings className="w-4 h-4" />
                          Configure Integration
                        </Button>
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-950/30 flex items-center justify-center mb-5">
                          <Cloud className="w-7 h-7 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="text-base font-normal text-gray-900 dark:text-white mb-1">
                          ServiceTitan API Import
                        </h3>
                        <div className="flex items-center gap-1.5 mb-4">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                          <span className="text-xs text-green-600 dark:text-green-400">
                            Connected
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
                          Import customers and invoices directly from your
                          ServiceTitan account using your configured API
                          credentials.
                        </p>
                        <Button
                          disabled={isApiImporting || isImportInProgress}
                          onClick={handleApiImport}
                        >
                          <Cloud className="w-4 h-4" />
                          {isApiImporting
                            ? "Importing..."
                            : isImportInProgress
                              ? "Import in Progress..."
                              : "Import from API"}
                        </Button>
                        {isImportInProgress && !isApiImporting && (
                          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                            A data import is currently being processed.
                          </p>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-5">
                      <Cloud className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-base font-normal text-gray-900 dark:text-white mb-2">
                      ServiceTitan API Import
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                      Data import is managed by your CertainPath administrator.
                      Contact your administrator to configure or trigger a data
                      import.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeIntegration === "quickbooks_online" && (
          <div className="flex items-center justify-center min-h-[60vh]">
            <Card className="max-w-md w-full">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                {canManageIntegrations ? (
                  <>
                    {integrationsStatus?.quickBooksOnlineIntegration
                      ?.isConfigured ? (
                      <>
                        <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-950/30 flex items-center justify-center mb-5">
                          <Cloud className="w-7 h-7 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="text-base font-normal text-gray-900 dark:text-white mb-1">
                          QuickBooks Online API Import
                        </h3>
                        <div className="flex items-center gap-1.5 mb-4">
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${integrationsStatus?.quickBooksOnlineIntegration?.isConnected ? "bg-green-500" : "bg-red-500"}`}
                          />
                          <span
                            className={`text-xs ${integrationsStatus?.quickBooksOnlineIntegration?.isConnected ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                          >
                            {integrationsStatus?.quickBooksOnlineIntegration
                              ?.isConnected
                              ? "Connected"
                              : "Not Connected"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mb-6">
                          Import membership data directly from your QuickBooks
                          Online account using your configured membership SKUs.
                        </p>
                        {!integrationsStatus?.quickBooksOnlineIntegration
                          ?.isConnected ? (
                          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-left">
                            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                              QuickBooks Online is not connected. Please connect
                              your account via the Data Connector.
                            </p>
                          </div>
                        ) : !integrationsStatus?.quickBooksOnlineIntegration
                            ?.hasSyncableData ? (
                          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-left">
                            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                              No QuickBooks data available. Please run a sync
                              job via the Data Connector first.
                            </p>
                          </div>
                        ) : integrationsStatus?.quickBooksOnlineIntegration
                            ?.hasRunningJobs ? (
                          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-left">
                            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                              A QuickBooks sync job is currently running. Please
                              wait for it to complete before importing.
                            </p>
                          </div>
                        ) : (
                          <>
                            <Button
                              disabled={isQboImporting || isImportInProgress}
                              onClick={handleQboImport}
                            >
                              <Cloud className="w-4 h-4" />
                              {isQboImporting
                                ? "Importing..."
                                : isImportInProgress
                                  ? "Import in Progress..."
                                  : "Import from API"}
                            </Button>
                            {isImportInProgress && !isQboImporting && (
                              <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                                A data import is currently being processed.
                              </p>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-5">
                          <Cloud className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                        </div>
                        <h3 className="text-base font-normal text-gray-900 dark:text-white mb-2">
                          QuickBooks Online integration not configured
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                          Configure your QuickBooks Online integration to enable
                          data synchronization.
                        </p>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center mb-5">
                      <Cloud className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-base font-normal text-gray-900 dark:text-white mb-2">
                      QuickBooks Online API Import
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                      Data import is managed by your CertainPath administrator.
                      Contact your administrator to configure or trigger a data
                      import.
                    </p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mb-4" id="upload-status">
          {isUploading && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5">
                <div
                  className="bg-blue-500 h-2.5 rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmationDialog
        cancelMessage={cancelMessage}
        confirmMessage={confirmMessage}
        dialogInstructionFinalQuestion={dialogInstructionFinalQuestion}
        dialogInstructionItems={dialogInstructionItems}
        dialogInstructionTitle={dialogInstructionTitle}
        dialogTitleContent={dialogTitleContent}
        isOpen={showConfirmationModal}
        onClose={cancelHandleFunction}
        onConfirm={confirmHandleFunction}
      />

      <PurgeInvoicesModal
        isOpen={showPurgeConfirmationModal}
        isPurging={purgeLoading}
        onClose={handlePurgeCancelModal}
        onConfirm={handlePurgeConfirmModal}
      />
    </MainPageWrapper>
  );
};
